import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { ME_KEY, useMe } from '../session.js';
import { RecoveryCodes } from './RecoveryCodes.js';
import { AnyCode, codeError, RenewRecoveryCodes, SetupSecondFactor } from './SecondFactor.js';
import { Button, Card, LoadError, Loading, Notice } from './ui.js';

const MFA_KEY = ['auth', 'mfa'] as const;

type Step = 'idle' | 'disable' | 'renew';

/**
 * «Zwei-Faktor-Anmeldung» in the account (SP-D19; account.html): optional for customers – set up
 * with an authenticator app, ten recovery codes shown once, new codes and turned off – both only
 * with a code (review of SP-D21).
 * Operators keep it: the admin needs it.
 */
export function SecondFactorCard() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const me = useMe().data;
  const state = useQuery({ queryKey: MFA_KEY, queryFn: () => unwrap(api.GET('/auth/mfa')) });
  const [step, setStep] = useState<Step>('idle');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [done, setDone] = useState<'enabled' | 'disabled' | null>(null);
  const refresh = async () => {
    await client.invalidateQueries({ queryKey: MFA_KEY });
    await client.invalidateQueries({ queryKey: ME_KEY });
  };
  const disable = useMutation({
    mutationFn: (code: string) => unwrap(api.POST('/auth/mfa/disable', { body: { code } })),
    onSuccess: async () => {
      setStep('idle');
      setDone('disabled');
      await refresh();
    },
  });
  const head = (
    <h2>
      {t('account.mfa.title')} <small>{t('account.mfa.sub')}</small>
    </h2>
  );
  if (state.isPending)
    return (
      <Card>
        {head}
        <Loading />
      </Card>
    );
  if (state.isError)
    return (
      <Card>
        {head}
        <LoadError message={errorMessage(state.error, t)} onRetry={() => void state.refetch()} />
      </Card>
    );
  const { enabled, recoveryCodesLeft } = state.data;
  const operator = me?.user.isOperator ?? false;

  // The codes, after setting up or renewing them: shown once, until confirmed.
  if (codes)
    return (
      <Card>
        <h2>
          {t('mfa.codes.title')} <small>{t('mfa.codes.sub')}</small>
        </h2>
        <RecoveryCodes
          codes={codes}
          doneLabel={t('account.mfa.done')}
          onDone={() => {
            setCodes(null);
            void refresh();
          }}
        />
      </Card>
    );

  return (
    <Card className="mfa-card">
      {head}
      {done ? (
        <Notice kind="success">
          {done === 'enabled' ? t('account.mfa.enabled') : t('account.mfa.disabled')}
        </Notice>
      ) : null}
      {!enabled ? (
        <SetupSecondFactor
          intro={
            <p className="state">
              <span className="badge nodot neutral">{t('account.mfa.off')}</span>
              <span className="hint">{t('account.mfa.intro')}</span>
            </p>
          }
          onEnabled={(recoveryCodes) => {
            setDone('enabled');
            setCodes(recoveryCodes);
          }}
        />
      ) : (
        <>
          <p className="state">
            <span className="badge nodot ok">
              {t('account.mfa.on', { count: recoveryCodesLeft })}
            </span>
            {operator ? <span className="hint">{t('account.mfa.adminNote')}</span> : null}
          </p>
          {step === 'renew' ? (
            <RenewRecoveryCodes
              onCodes={(recoveryCodes) => {
                setStep('idle');
                setCodes(recoveryCodes);
              }}
              onCancel={() => setStep('idle')}
            />
          ) : step === 'disable' ? (
            <div className="confirm">
              <AnyCode
                head={() => (
                  <>
                    <b>{t('account.mfa.disableQ')}</b>
                    <span className="hint">{t('account.mfa.disableText')}</span>
                  </>
                )}
                submitLabel={t('account.mfa.disable')}
                variant="danger"
                onSubmit={(code) => disable.mutate(code)}
                busy={disable.isPending}
                error={disable.error ? codeError(disable.error, t) : undefined}
                onSwitch={() => disable.reset()}
                aside={
                  <Button
                    variant="secondary"
                    onClick={() => {
                      disable.reset();
                      setStep('idle');
                    }}
                  >
                    {t('app.cancel')}
                  </Button>
                }
              />
            </div>
          ) : (
            <div className="acts">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setDone(null);
                  setStep('renew');
                }}
              >
                {t('account.mfa.renew')}
              </Button>
              {operator ? null : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setDone(null);
                    setStep('disable');
                  }}
                >
                  {t('account.mfa.disable')}
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
}
